/**
 * 涉及服务端数据与前端状态数据的转换函数
 */

import type {
  AnnoSourceType,
  Attr,
  Element,
  ElementAnno,
  ElementAttrValues,
  Label,
  RawdataAnnoObject,
  WidgetObject,
} from '@/types';

import { AnnoCompoundItem, AnnoInstanceItem, IAnnoItem } from '../annos';

/**
 * 把服务端的标注物转换成前端定义的标注物
 * @param data 服务端的标注数据
 * @param label 服务端返回的标签数据
 * @returns 前端定义的标注物
 */
export const transInAnno = (data: RawdataAnnoObject, label: Label): IAnnoItem | null => {
  const {
    uuid,
    track_id: trackId,
    label: { widget, attrs: attrList },
    source: _source = 'manual',
    compound,
  } = data;

  const attrs =
    attrList?.reduce((prev, cur) => {
      return {
        ...prev,
        [cur.name]: cur.values,
      };
    }, {}) ?? {};

  if (compound) {
    return new AnnoCompoundItem(uuid, label.name, compound.parts, {
      source: _source as AnnoSourceType,
      trackId,
      attrs,
    });
  } else {
    return new AnnoInstanceItem(uuid, label.name, widget as WidgetObject, {
      source: _source as AnnoSourceType,
      trackId,
      attrs,
    });
  }
};

/**
 * 把前端定义的标注物转换成服务端定义的标注物
 * @param obj 前端的标注物
 * @returns 服务端定义的标注物
 */
export const transOutAnno = (obj: IAnnoItem): (RawdataAnnoObject & { mapped?: boolean }) | null => {
  if (!obj.label) return null;

  const objAttrs = obj.attrs;
  const attrs = [];
  for (let key in objAttrs) {
    attrs.push({
      name: key,
      values: objAttrs[key],
    });
  }

  if (obj instanceof AnnoInstanceItem) {
    return {
      uuid: obj.name,
      track_id: obj.trackId,
      label: {
        name: obj.label,
        widget: obj.widget,
        attrs,
      },
      source: obj.source,
    };
  } else {
    return {
      uuid: obj.name,
      track_id: obj.trackId,
      label: {
        name: obj.label,
        attrs,
      },
      source: obj.source,
      compound: {
        parts: obj.compound,
      },
    };
  }
};

// 转换帧属性的数据结构，数组变成对象
export const transInElementAttrs = (element: Element, attr: ElementAnno['attrs']) => {
  const attrValues: ElementAttrValues = {};
  if (element && attr) {
    attr.forEach(({ name, values }) => {
      attrValues[name] = values;
    });
  }
  return attrValues;
};

// 对象变成数组
export const transOutElementAttrs = (attrValues: ElementAttrValues) => {
  const attrs = [];
  for (let key in attrValues) {
    attrs.push({
      name: key,
      values: attrValues[key],
    });
  }
  return attrs;
};

export const transInAttrDefault = (attrs: Record<string, Attr>, name: Attr['name'], originDefault?: string) => {
  if (attrs[name].type === 'input') {
    return [originDefault ?? ''];
  }

  let defaultValues: string[] = [];

  // 有默认输入的选项
  if (originDefault) {
    // 单选和多选，originDefault 属性配置时有可能写 display_name 或 name，这里统一转成 name
    // TODO: 属性配置填写 default 值的时候，用 choices 生成 options 选择，就不用转换了
    const values = originDefault.split(',');
    values.forEach((value, index) => {
      const defaultName = attrs[name].choices.find(
        ({ display_name, name }) => value === display_name || value === name
      )?.name;
      if (defaultName) {
        defaultValues.push(defaultName);
      }
    });
  } else {
    // 默认选中第一个
    defaultValues.push(attrs[name].choices[0].name);
  }

  return defaultValues;
};
